import React from 'react';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { formatFairFeeRange, formatNumber, getCurrencyLogo } from '../../../../utils/global';
import CustomTooltip from '../../../common-components/CustomTooltip/CustomTooltip';
import '../styles/profile.scss';
import ShortString from '@/components/common-components/ShortString/ShortString';
import InfoCircleIcon from '@/components/Icons/icons/InfoCircleIcon';
import { fairFeeCalculation, getRangeForFairFee } from '@Saral-Influencer-Marketing/saral-utils/dist/functions/formulas/fairFee';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';


export default function ProfileStatsCards({ userDetails, audienceCredebility, profile }) {
  const { userSettings } = useSelector((state) => state.user);
  const engagementRate = userDetails?.engagement_rate
    ? Number(userDetails?.engagement_rate)
    : profile?.engagement_rate
      ? Number(profile?.engagement_rate)
      : 0;

  const statHistory = userDetails?.stat_history;
  const latestStat = statHistory?.[statHistory?.length - 1];

  const followers = latestStat?.followers
    ? Number(latestStat?.followers)
    : profile?.followers
      ? Number(profile?.followers)
      : 0;
  const totalPosts = userDetails?.posts_count;
  const avgViews = userDetails?.avg_reels_plays || userDetails?.avg_views;
  const totalViews = avgViews ? avgViews * totalPosts : 0;

  const fairFee = fairFeeCalculation({
    followers: followers ? Number(followers) : null,
    idealCpm: userSettings?.paidMediaCPMBenchmark?.value ? Number(userSettings?.paidMediaCPMBenchmark?.value) : 25,
    platform: profile?.platform,
    engagementRate: engagementRate ? Number(engagementRate / 100) : null,
    avgViews: avgViews ? Number(avgViews) : null,
  });

  const fairFeeRange = getRangeForFairFee(fairFee);

  const currencyLogo = getCurrencyLogo(userSettings?.paidMediaCPMBenchmark?.currency);
  const fairFeeText = formatFairFeeRange(fairFeeRange.min, fairFeeRange.max, currencyLogo);

  const last30DaysPosts = userDetails?.recent_posts?.filter((post) => {
    const postDate = dayjs(post?.created);
    return postDate.isAfter(dayjs().subtract(30, 'days'));
  });

  return (
    <div className="profile-stats-container">
      <div className="profile-stats-row">
        <ProfileDetailCard
          name="Eng. Rate"
          value={engagementRate ? `${(engagementRate * 100)?.toFixed(2)}%` : '-'}
          info="What % of followers engage with content"
        />
        <ProfileDetailCard
          name="Avg. Likes"
          value={userDetails?.avg_likes ? formatNumber(userDetails?.avg_likes) : '-'}
          info="Avg. number of likes on posts"
        />
        <ProfileDetailCard
          name="Avg. Cmts."
          value={userDetails?.avg_comments ? formatNumber(userDetails?.avg_comments) : '-'}
          info="Avg. number of comments on posts"
        />
        <ProfileDetailCard
          name="PPP"
          value={
            !isNaN(userDetails?.paid_post_performance) ? Number(userDetails?.paid_post_performance).toFixed(4) : '0'
          }
          info="Paid post performance vs. Organic"
        />
      </div>
      <div className="profile-stats-row">
        <ProfileDetailCard name="Audience Qty." value={userDetails?.audience_qty ? formatNumber(userDetails?.audience_qty) : '-'} />
        <ProfileDetailCard
          name="Est. Fair Fee"
          value={followers ? fairFeeText : '-'}
          info="Estimated fair fee based on all metrics"
        />
        <ProfileDetailCard
          name="Total Views"
          value={totalViews ? formatNumber(totalViews) : '-'}
        />
        <ProfileDetailCard name="Avg Views" value={avgViews ? formatNumber(avgViews) : '-'} />
      </div>
      <div className="profile-stats-row">
        <ProfileDetailCard
          name="Diehard fans"
          value={audienceCredebility ? audienceCredebility?.toFixed(2) + '%' : '-'}
          info="% of likers most likely to act on your offer"
        />
        <ProfileDetailCard name="30 Days Reach" value="-" info="Reach in the last month" />
        <ProfileDetailCard
          name="30 Days Posts"
          value={last30DaysPosts?.length ? last30DaysPosts?.length : '-'}
          info="Number of posts in the last month"
        />
        <ProfileDetailCard
          name="Avg Reel Plays"
          value={userDetails?.avg_reels_plays ? formatNumber(userDetails?.avg_reels_plays) : '-'}
          info="How many people watch reels"
        />
      </div>
    </div>
  );
}

const ProfileDetailCard = ({ name, value, info }) => {
  return (
    <div className="detail-card">
      <p className="detail-name flex items-center gap-1">
        {name}{' '}
        {info && (
          <CustomTooltip title={info} arrow sx={{ zIndex: 99 }}>
            <InfoCircleIcon width={16} height={16} />
          </CustomTooltip>
        )}{' '}
      </p>
      <p className="detail-value">
        <ShortString name={value} placement="bottom" stringLength={10} />{' '}
      </p>
    </div>
  );
};